/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useEffect, useRef, useState } from 'react';
import { LatLngBounds } from 'leaflet';
import { useMap } from 'react-leaflet';
import { areaSelect, LeafletAreaSelect } from 'src/components/leaflet-areaselect';
import { Fullscreen, Minimize2, PencilRuler, X } from 'lucide-react';

import { classNames } from 'primereact/utils';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';

import { Utils } from '../../../utils/utils';
import { BBOXUtils } from './utils';
import 'leaflet/dist/leaflet.css';
import '../../../components/leaflet-areaselect/leaflet-areaselect.css';

import styled from 'styled-components';

interface BBoxMapControlerProps {
  className?: string;
  setBBox: (array?: number[]) => void;
  defaultBound: LatLngBounds;
}

const DivControl = styled.div`
  .map-control-element {
    height: 36px;
  }
`;

export const BBoxMapControler: React.FC<BBoxMapControlerProps> = ({ className, setBBox, defaultBound }) => {
  const map = useMap();

  const [isFullscreen, setIsFullScreen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const btnRef = useRef<HTMLButtonElement>(null);
  const inputBBoxRef = useRef<HTMLInputElement>(null);
  const isMounted = useRef<boolean>(false);

  const areaSelection = useRef<LeafletAreaSelect>(
    areaSelect({
      width: 250,
      height: 250,
      keepAspectRatio: false
    })
  );

  useEffect(() => {
    areaSelection.current.addTo(map);
    if (defaultBound) {
      areaSelection.current.setBounds(defaultBound, false);
      isMounted.current = true;
    }
    const handleChange = (ev) => {
      const target = ev.target as LeafletAreaSelect;
      const bounds = target.getBounds();
      const bbox = BBOXUtils.boundsToArray(bounds);
      setBBox(bbox);
    };

    areaSelection.current.on('change', handleChange);
    return () => {
      areaSelection.current.off('change');
    };
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      areaSelection.current.setBounds(defaultBound, false);
      areaSelection.current.clearOffManualBounds();
    }
  }, [defaultBound]);

  const onSubmitBBoxText = () => {
    const parsedBBox = BBOXUtils.parseBBoxString(inputBBoxRef.current?.value || '');
    if (parsedBBox) {
      setBBox(parsedBBox);
      areaSelection.current.setBounds(BBOXUtils.arrayToLatLngBounds(parsedBBox), false);
      areaSelection.current.clearOffManualBounds();
    }
  };

  return (
    <DivControl className={classNames('mt-1 leaflet-top leaflet-right', className)}>
      <div className="leaflet-control flex flex-column align-items-end">
        <button
          type="button"
          onClick={() => {
            const mapContainer = map.getContainer();
            if (!isFullscreen) {
              Utils.elementToFullscreen(mapContainer);
            } else {
              document.exitFullscreen();
            }
            setIsFullScreen(!isFullscreen);
          }}
          ref={btnRef}
          className="cursor-pointer w-fit py-1 border-none map-control-element "
        >
          {isFullscreen ? <Minimize2 /> : <Fullscreen />}
        </button>

        <div className="flex align-items-center mt-2">
          <div className="flex">
            {isEditing && (
              <div className="p-inputgroup p-inputgroup-sm map-control-element fadein transition-duration-1 flex-1 mr-3">
                <InputText
                  onMouseMove={(e) => {
                    e.stopPropagation();
                  }}
                  ref={inputBBoxRef}
                  defaultValue={defaultBound ? JSON.stringify(BBOXUtils.boundsToArray(defaultBound)) : ''}
                  className="p-inputtext-sm"
                  placeholder="ex: [-180, -90, 180, 90]"
                />
                <Button
                  icon="pi pi-pencil"
                  type="button"
                  onClick={onSubmitBBoxText}
                  size="small"
                  className="p-button-primary"
                />
              </div>
            )}
          </div>
          {isEditing && (
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              className="cursor-pointer block py-1 border-none map-control-element "
            >
              <X />
            </button>
          )}
          {!isEditing && (
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="cursor-pointer block py-1 border-none map-control-element "
            >
              <PencilRuler />
            </button>
          )}
        </div>
      </div>
    </DivControl>
  );
};
